import { Editor, JSONContent } from '@tiptap/core';
import { MIN_FONT_SIZE } from 'constants/fonts';
import { findAllPropertyValues } from 'utils/findAllPropertyValues';
import { roundToDecimals } from 'utils/number';

import { formatLetterSpacing } from './TextWidget.helpers';
import { containsList } from './TextWidget.helpers';
import { TextWidgetData } from './TextWidget.types';
import { extensions } from './TextWidgetExtensions';

/**
 * From the given ProseMirror data
 * extracts text properties such as text, font size, boldness, list presence, and font family
 * @param proseMirrorData - the ProseMirror data
 * @returns the text properties
 */
export const getTextWidgetProperties = (proseMirrorData: JSONContent) => {
  const editor = new Editor({ extensions: extensions, content: proseMirrorData, editable: false });
  editor.commands.selectAll();

  const text = editor.getText();
  const fontSize = roundToDecimals(parseFloat(editor?.getAttributes('textStyle')?.fontSize), 2);
  const isEntireTextBold = !!editor.isActive('bold');
  const hasList = containsList(editor);
  const fontFamily = editor.getAttributes('textStyle').fontFamily;

  editor.destroy();

  return { text, fontSize, isEntireTextBold, hasList, fontFamily };
};

const RESIZE_FONT_TRESHOLD = 1;
const FONT_SIZE_STEP = 0.1;
const MAX_ITERATIONS = 800;

/**
 * Sets the font family of a TextWidgetData object and adjusts the font size to fit the height
 *
 * @param widgetData - The TextWidgetData object to modify.
 * @param newFontFamily - The new font family to set.
 *
 * @returns An object containing the new height in pixels and the updated proseMirrorData.
 */
export const setFontFamily = (widgetData: TextWidgetData, newFontFamily: string) => {
  const { letterSpacing = 0, proseMirrorData, widthPx } = widgetData;

  const formattedLetterSpacing = formatLetterSpacing(letterSpacing);
  // the initial font size
  const initialFontSize = roundToDecimals(
    parseFloat(findAllPropertyValues(proseMirrorData, ['fontSize'])[0] as string),
    2,
  );
  const initialFontFamily = findAllPropertyValues(proseMirrorData, ['fontFamily'])[0];

  // render the editor in a hidden container so that we can measure the height
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.top = '-100%';
  container.style.left = '-100%';
  container.style.width = widthPx + 'px';
  container.style.height = 'fit-content';
  container.style.opacity = '0';
  container.style.fontSize = initialFontSize + 'px';
  container.style.letterSpacing = formattedLetterSpacing;
  container.style.fontFamily = initialFontFamily as string;
  document.body.appendChild(container);

  // create the tip tap editor
  const editor = new Editor({ extensions: extensions, content: proseMirrorData, editable: false, element: container });
  // the height is not accurate if the text is not selected and focused
  editor.setEditable(true);
  editor.commands.focus('all');

  // store the old height here
  const initialHeightPx = editor?.view.dom.clientHeight ?? 0;

  // set the new font family
  container.style.fontFamily = newFontFamily;
  editor.commands.setFontFamily(newFontFamily);

  const newHeightPx = editor?.view.dom.clientHeight ?? 0;
  // the height difference of the text with the new font family compared to the initial font family
  const heightDiff = newHeightPx - initialHeightPx;

  // if the height difference is less than 1px, we don't need to adjust the font size
  const shouldIncreaseFontSize = heightDiff > RESIZE_FONT_TRESHOLD;
  const shouldDecreaseFontSize = heightDiff < -RESIZE_FONT_TRESHOLD;

  // the height is less so we need to increase the font size
  if (shouldIncreaseFontSize || shouldDecreaseFontSize) {
    // adjust the fontsize so that it fits the old height
    let measuredHeightDiff = heightDiff;
    // keep track of the iterations and break if it exceeds the max iterations just to be safe
    let i = 0;
    // the adjusted font size
    let newAdjustedFontSize;

    // keep track of the previous font in case the height difference is closer to 0 (can happen with line breaks)
    let previousFontSize = initialFontSize;
    let previousHeightDiff = undefined;

    while (
      // limit it to MAX_ITERATIONS just in case
      i < MAX_ITERATIONS &&
      (shouldIncreaseFontSize ? measuredHeightDiff > RESIZE_FONT_TRESHOLD : measuredHeightDiff < -RESIZE_FONT_TRESHOLD)
    ) {
      const newFontSize = Math.max(
        shouldIncreaseFontSize ? previousFontSize - FONT_SIZE_STEP : previousFontSize + FONT_SIZE_STEP,
        MIN_FONT_SIZE,
      );

      newAdjustedFontSize = roundToDecimals(newFontSize, 2);
      editor?.commands.focus('all');
      editor.commands.setFontSize(newAdjustedFontSize + 'px');

      // measure the new height
      const newHeightPx = editor?.view.dom.clientHeight ?? 0;
      measuredHeightDiff = newHeightPx - initialHeightPx;
      i++;

      // should not be smaller than the min font size
      // we're not checking the max size since font sizes can be larger than the max size
      if (newFontSize === MIN_FONT_SIZE) break;

      // keep track of previous height in case there is a line break and its closer
      if (measuredHeightDiff > RESIZE_FONT_TRESHOLD) {
        previousFontSize = newAdjustedFontSize;
        previousHeightDiff = measuredHeightDiff;
      }
    }

    // if the previousHeightDiff is closer to 0 than the current heightDiff - use the previousFontSize
    // can happen if there is a line break
    if (
      typeof previousHeightDiff === 'number' &&
      Math.abs(previousHeightDiff) < Math.abs(measuredHeightDiff) &&
      previousHeightDiff < 5
    ) {
      newAdjustedFontSize = previousFontSize;
      measuredHeightDiff = previousHeightDiff;
      editor?.commands.focus('all');
      editor.commands.setFontSize(previousFontSize + 'px');
    }

    // return the new height too (it can differ by a few pixels)
    const heightPx = editor?.view.dom.clientHeight ?? 0;

    editor.destroy();
    container.remove();

    return { heightPx, proseMirrorData: editor.getJSON() };
  }

  editor.destroy();
  container.remove();
  return { heightPx: newHeightPx, proseMirrorData: editor.getJSON() };
};
